/**
 * Admin Console — KYC Reviews queue.
 *
 * Consumes /api/admin/kyc/reviews and /api/admin/kyc/reviews/:id.
 * An authorised reviewer (system_admin, compliance_officer,
 * identity_officer) can Approve / Reject / Request Replacement /
 * Escalate every borderline enrolment.
 *
 * The list view carries a subset of fields per row (applicant name,
 * reason, doc, country, when it entered the queue). Click into a row
 * to see the full engine snapshot: OCR confidence, face-match
 * similarity, liveness signals, MRZ verdict, applied policies.
 *
 * Zero-Knowledge invariant: this page NEVER shows raw document images
 * or embeddings — the backend row does not carry them.
 */
import React from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const REASON_HINTS = {
  LOW_CONFIDENCE:
    "One or more signals came in below the confidence floor. Inspect OCR, face-match, and liveness confidences before deciding.",
  MANUAL_REVIEW:
    "An adapter explicitly deferred to human review (e.g. name transliteration ambiguity).",
  REVIEW_REQUIRED:
    "A jurisdiction-specific rule requires a human sign-off before issuing this identity.",
};

const STATE_BADGES = {
  pending:            { bg: "#7c3aed", label: "PENDING"          },
  approved:           { bg: "#059669", label: "APPROVED"         },
  rejected:           { bg: "#dc2626", label: "REJECTED"         },
  needs_replacement:  { bg: "#d97706", label: "REPLACEMENT ASKED"},
  escalated:          { bg: "#0284c7", label: "ESCALATED"        },
};

export default function AdminKycReviews() {
  const { user } = useAuth();
  const [state, setState] = React.useState("pending");
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [openRow, setOpenRow] = React.useState(null);
  const [total, setTotal] = React.useState(0);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(
        `/admin/kyc/reviews?state=${state}&limit=200`
      );
      setRows(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error(`Could not load reviews — ${e?.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  }, [state]);

  React.useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="admin-kyc-reviews-page" className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-white">KYC Reviews</h1>
          <p className="text-white/50 text-sm">
            Borderline enrolments awaiting human decision. Signed in as{" "}
            <span className="text-white/80">{user?.email}</span> ({user?.role}).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <StateFilter value={state} onChange={setState} />
          <button
            className="rounded-lg bg-white/10 hover:bg-white/20 text-white px-3 py-1.5 text-sm"
            onClick={load}
            data-testid="admin-kyc-refresh"
          >
            Refresh
          </button>
        </div>
      </div>

      <div className="rounded-2xl border border-white/10 bg-black/30">
        <table className="w-full text-sm text-white/90">
          <thead className="bg-white/5 text-white/60 text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left px-4 py-3">When</th>
              <th className="text-left px-4 py-3">Applicant</th>
              <th className="text-left px-4 py-3">Doc</th>
              <th className="text-left px-4 py-3">Country</th>
              <th className="text-left px-4 py-3">Reason</th>
              <th className="text-left px-4 py-3">State</th>
              <th className="text-right px-4 py-3">Action</th>
            </tr>
          </thead>
          <tbody data-testid="admin-kyc-reviews-tbody">
            {loading && (
              <tr><td colSpan={7} className="px-4 py-6 text-white/50">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-white/50">
                No reviews in state <span className="text-white/80">{state}</span>.
              </td></tr>
            )}
            {!loading && rows.map((r) => (
              <tr key={r.review_id} className="border-t border-white/5 hover:bg-white/5">
                <td className="px-4 py-3 text-white/70">
                  {formatWhen(r.created_at)}
                </td>
                <td className="px-4 py-3">
                  <div className="font-medium">{r.applicant_full_name || "—"}</div>
                  {r.applicant_email && (
                    <div className="text-xs text-white/50">{r.applicant_email}</div>
                  )}
                </td>
                <td className="px-4 py-3 text-white/70">{r.doc_type}</td>
                <td className="px-4 py-3 text-white/70">{r.country}</td>
                <td className="px-4 py-3">
                  <ReasonPill code={r.decision_reason} />
                </td>
                <td className="px-4 py-3">
                  <StateBadge state={r.state} />
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    className="rounded-lg bg-white/10 hover:bg-white/20 px-3 py-1 text-xs"
                    onClick={() => setOpenRow(r.review_id)}
                    data-testid={`admin-kyc-open-${r.review_id}`}
                  >
                    Open
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="px-4 py-2 border-t border-white/5 text-xs text-white/50">
          {rows.length} of {total} shown
        </div>
      </div>

      {openRow && (
        <ReviewDrawer
          reviewId={openRow}
          onClose={() => setOpenRow(null)}
          onDecided={() => { setOpenRow(null); load(); }}
        />
      )}
    </div>
  );
}

function StateFilter({ value, onChange }) {
  return (
    <select
      className="rounded-lg bg-black/30 border border-white/10 text-white text-sm px-3 py-1.5"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid="admin-kyc-state-filter"
    >
      <option value="pending">Pending</option>
      <option value="approved">Approved</option>
      <option value="rejected">Rejected</option>
      <option value="needs_replacement">Replacement Asked</option>
      <option value="escalated">Escalated</option>
      <option value="all">All</option>
    </select>
  );
}

function StateBadge({ state }) {
  const meta = STATE_BADGES[state] || { bg: "#334155", label: (state || "").toUpperCase() };
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold text-white"
      style={{ background: meta.bg }}
    >
      {meta.label}
    </span>
  );
}

function ReasonPill({ code }) {
  return (
    <span
      title={REASON_HINTS[code] || code}
      className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] bg-amber-500/20 border border-amber-500/40 text-amber-100"
    >
      {code}
    </span>
  );
}

function formatWhen(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}

function ReviewDrawer({ reviewId, onClose, onDecided }) {
  const [row, setRow] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [notes, setNotes] = React.useState("");
  const [busy, setBusy] = React.useState(null);
  const [escalateTo, setEscalateTo] = React.useState("compliance_officer");

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(
          `/admin/kyc/reviews/${reviewId}`
        );
        if (!cancelled) setRow(r.data);
      } catch (e) {
        toast.error(`Could not load review — ${e?.response?.data?.detail || e.message}`);
        onClose();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [reviewId, onClose]);

  async function decide(action) {
    setBusy(action);
    try {
      const body = { action, notes: notes || null };
      if (action === "escalate") body.escalate_to = escalateTo;
      const r = await api.post(
        `/admin/kyc/reviews/${reviewId}/decide`,
        body
      );
      const vlk = r.data.issued_vlk_id;
      toast.success(
        action === "approve" && vlk
          ? `Approved. VLK_ID issued: ${vlk}`
          : `Decision recorded: ${action}`
      );
      onDecided();
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === "object" ? (d.reason_code || JSON.stringify(d)) : d || e.message;
      toast.error(`Decision failed — ${msg}`);
    } finally {
      setBusy(null);
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex justify-end"
      onClick={onClose}
      data-testid="admin-kyc-drawer-backdrop"
    >
      <div
        className="w-full max-w-2xl h-full overflow-y-auto bg-[#0b0f1a] border-l border-white/10 p-6 text-white/90"
        onClick={(e) => e.stopPropagation()}
        data-testid="admin-kyc-drawer"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">KYC Review</h2>
          <button
            className="text-white/60 hover:text-white text-xl"
            onClick={onClose}
            data-testid="admin-kyc-drawer-close"
          >
            ✕
          </button>
        </div>

        {loading && <div className="text-white/50">Loading review…</div>}

        {!loading && row && (
          <>
            <div className="mb-4">
              <div className="text-xs uppercase text-white/50">Review ID</div>
              <div className="font-mono text-xs break-all">{row.review_id}</div>
            </div>

            <SectionCard title="Applicant (typed)">
              <KV k="Full name" v={row.applicant?.full_name} />
              <KV k="DOB" v={row.applicant?.date_of_birth} />
              <KV k="Email" v={row.applicant?.email} />
              <KV k="Phone" v={row.applicant?.phone} />
              <KV k="Document" v={row.doc_type} />
              <KV k="Country" v={row.country} />
            </SectionCard>

            <SectionCard title="Decision Engine Snapshot">
              {row.engine_snapshot ? (
                <pre className="text-[11px] bg-black/50 border border-white/10 rounded-lg p-3 overflow-x-auto">
                  {JSON.stringify(row.engine_snapshot, null, 2)}
                </pre>
              ) : (
                <div className="text-white/50">Engine snapshot absent.</div>
              )}
              <div className="mt-2">
                <ReasonPill code={row.decision_reason} />
              </div>
            </SectionCard>

            {row.state !== "pending" && (
              <SectionCard title="Prior decision">
                <KV k="State" v={row.state} />
                <KV k="Action" v={row.action} />
                <KV k="Decided by" v={row.decided_by} />
                <KV k="Decided at" v={formatWhen(row.decided_at)} />
                <KV k="Notes" v={row.notes} />
                {row.escalated_to && <KV k="Escalated to" v={row.escalated_to} />}
                {row.issued_vlk_id && <KV k="VLK_ID issued" v={row.issued_vlk_id} />}
              </SectionCard>
            )}

            {row.state === "pending" && (
              <>
                <SectionCard title="Reviewer notes">
                  <textarea
                    className="w-full h-24 bg-black/30 border border-white/10 rounded-lg p-2 text-sm"
                    placeholder="Optional context recorded in the audit trail."
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    data-testid="admin-kyc-notes"
                  />
                </SectionCard>

                <SectionCard title="Escalation target (if escalating)">
                  <select
                    className="w-full bg-black/30 border border-white/10 rounded-lg p-2 text-sm"
                    value={escalateTo}
                    onChange={(e) => setEscalateTo(e.target.value)}
                    data-testid="admin-kyc-escalate-to"
                  >
                    <option value="compliance_officer">compliance_officer</option>
                    <option value="security_officer">security_officer</option>
                    <option value="identity_officer">identity_officer</option>
                  </select>
                </SectionCard>

                <div className="grid grid-cols-2 gap-3 mt-4">
                  <button
                    className="rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white px-4 py-2 disabled:opacity-40"
                    onClick={() => decide("approve")}
                    disabled={!!busy}
                    data-testid="admin-kyc-approve"
                  >
                    {busy === "approve" ? "Approving…" : "Approve → issue VLK_ID"}
                  </button>
                  <button
                    className="rounded-xl bg-red-600 hover:bg-red-500 text-white px-4 py-2 disabled:opacity-40"
                    onClick={() => decide("reject")}
                    disabled={!!busy}
                    data-testid="admin-kyc-reject"
                  >
                    {busy === "reject" ? "Rejecting…" : "Reject"}
                  </button>
                  <button
                    className="rounded-xl bg-amber-600 hover:bg-amber-500 text-white px-4 py-2 disabled:opacity-40"
                    onClick={() => decide("request_replacement")}
                    disabled={!!busy}
                    data-testid="admin-kyc-replacement"
                  >
                    {busy === "request_replacement" ? "Requesting…" : "Request replacement"}
                  </button>
                  <button
                    className="rounded-xl bg-sky-600 hover:bg-sky-500 text-white px-4 py-2 disabled:opacity-40"
                    onClick={() => decide("escalate")}
                    disabled={!!busy}
                    data-testid="admin-kyc-escalate"
                  >
                    {busy === "escalate" ? "Escalating…" : "Escalate"}
                  </button>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function SectionCard({ title, children }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-4 mb-4">
      <div className="text-xs uppercase tracking-wide text-white/50 mb-2">{title}</div>
      {children}
    </div>
  );
}

function KV({ k, v }) {
  return (
    <div className="flex justify-between text-sm mb-1">
      <div className="text-white/50">{k}</div>
      <div className="text-white/90 text-right break-all max-w-[70%]">
        {v == null || v === "" ? "—" : String(v)}
      </div>
    </div>
  );
}
